import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Heart, MessageCircle, User, Bell, Check, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useSocket } from '@/hooks/useSocket'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

const iconMap: Record<string, React.ReactNode> = {
  like: <Heart className="w-4 h-4 text-red-500" />,
  comment: <MessageCircle className="w-4 h-4 text-blue-500" />,
  message: <Bell className="w-4 h-4 text-green-500" />,
  follow: <User className="w-4 h-4 text-purple-500" />,
  repost: <Bell className="w-4 h-4 text-orange-500" />,
}

export default function Notifications() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const socket = useSocket()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications').then(r => r.data)
  })

  // Real-time socket listener
  useEffect(() => {
    if (!socket) return
    const handleNew = (notification: any) => {
      queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old) return old
        return {
          ...old,
          unreadCount: (old.unreadCount || 0) + 1,
          notifications: [notification, ...old.notifications]
        }
      })
    }
    socket.on('notification:new', handleNew)
    return () => { socket.off('notification:new', handleNew) }
  }, [socket, queryClient])

  const readAll = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const readOne = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const deleteOne = useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold">{t('notifications.title')}</h1>
        {data?.unreadCount > 0 && (
          <Button variant="ghost" size="sm" onClick={() => readAll.mutate()}>
            <Check className="w-4 h-4 mr-1" /> {t('notifications.markAllRead')}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {data?.notifications?.map((n: any) => (
          <motion.div
            key={n._id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
              n.read ? 'bg-card border-border' : 'bg-primary/5 border-primary/20'
            }`}
          >
            {n.sender ? (
              <Avatar className="w-8 h-8 shrink-0">
                <AvatarImage src={n.sender.avatar} />
                <AvatarFallback className="text-xs">{n.sender.username?.[0]}</AvatarFallback>
              </Avatar>
            ) : (
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                {iconMap[n.type] || <Bell className="w-4 h-4" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm">{n.message}</p>
              <span className="text-xs text-muted-foreground">{formatDistanceToNow(n.createdAt)}</span>
            </div>
            <div className="flex gap-1">
              {!n.read && (
                <button onClick={() => readOne.mutate(n._id)} className="p-1.5 rounded-lg hover:bg-secondary">
                  <Check className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <button onClick={() => deleteOne.mutate(n._id)} className="p-1.5 rounded-lg hover:bg-destructive/10">
                <Trash2 className="w-4 h-4 text-destructive" />
              </button>
            </div>
          </motion.div>
        ))}
        {(!data?.notifications || data.notifications.length === 0) && (
          <div className="text-center py-12 text-muted-foreground">
            <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('notifications.empty')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
